import { useEffect } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import {
  GetSkipCategoryPreferencesQuery,
  GetSkipCategoryPreferencesQueryVariables,
  UpdateSkipCategoryPreferencesMutation,
  UpdateSkipCategoryPreferencesMutationVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import {
  GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
  UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER,
} from './ParentalControlsService'
import {
  buildChildrenMap,
  buildParentMap,
  buildPreferenceTree,
  countLeaves,
  countSkippedLeaves,
  flattenPreferenceTree,
  getAncestorIdsFromMap,
  getDescendantIdsFromMap,
  getLeafDescendantIds,
} from './preference-tree'

export type SkipCategoryPreferenceItem = NonNullable<
  NonNullable<NonNullable<GetSkipCategoryPreferencesQuery['user']>['parentalControlPreferences']>[number]
>

type UseSkipCategoryPreferencesParams = {
  lang: string
  contentId?: string
}

export const useSkipCategoryPreferences = ({ lang, contentId }: UseSkipCategoryPreferencesParams) => {
  const { cache } = useApolloClient()
  const { data, loading, error, refetch } = useQuery<
    GetSkipCategoryPreferencesQuery,
    GetSkipCategoryPreferencesQueryVariables
  >(GET_SKIP_CATEGORY_PREFERENCES_FOR_USER, {
    variables: { language: lang, ...(contentId ? { id: contentId } : {}) },
    context: { requireAuth: true },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (error) {
      const expectedMessages = ['language not supported', 'not_found']
      const isExpectedError = expectedMessages.some((msg) => error.message?.toLowerCase().includes(msg))
      if (!isExpectedError) {
        logger().error('Failed to fetch skip category preferences', { lang }, error)
      }
    }
  }, [error, lang])

  const [executeUpdate, { loading: updating }] = useMutation<
    UpdateSkipCategoryPreferencesMutation,
    UpdateSkipCategoryPreferencesMutationVariables
  >(UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER)
  const rawPreferences = (data?.user?.parentalControlPreferences ?? []).filter(isDefined)

  const tree = buildPreferenceTree(rawPreferences)
  const orderedPreferences = flattenPreferenceTree(tree)

  const queryVariables = { language: lang, ...(contentId ? { id: contentId } : {}) }

  const updateSkipPreference = async (categoryId: number, skipped: boolean) => {
    // Read current cache to get the latest state (not stale closure from render)
    const cachedData = cache.readQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
      query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
      variables: queryVariables,
    })
    const existingPrefs = (cachedData?.user?.parentalControlPreferences ?? []).filter(isDefined)
    const userId = cachedData?.user?.id ?? ''
    const needsReview = cachedData?.user?.needsReview ?? true

    // Pre-compute lookup maps for O(1) traversals instead of repeated linear scans
    const childrenMap = buildChildrenMap(existingPrefs)
    const parentMap = buildParentMap(existingPrefs)
    const descendantIds = getDescendantIdsFromMap(categoryId, childrenMap)

    // Build optimistic preferences: update the target and all its descendants
    const idsToUpdate = new Set<number>([categoryId, ...descendantIds])

    // Ancestors need their skippedCount recalculated
    const ancestorIds = new Set<number>(getAncestorIdsFromMap(categoryId, parentMap))

    // Pre-compute leaf descendants for each ancestor using the children map
    const prefMap = new Map(existingPrefs.map((p) => [p.id, p]))
    const ancestorLeafMap = new Map<number, typeof existingPrefs>()
    for (const ancestorId of ancestorIds) {
      const leafIds = getLeafDescendantIds(ancestorId, childrenMap)
      ancestorLeafMap.set(ancestorId, leafIds.map((id) => prefMap.get(id)).filter(isDefined))
    }

    const optimisticPrefs = existingPrefs.map((p) => {
      if (idsToUpdate.has(p.id)) {
        // For nodes with descendants, update their skippedCount to reflect all-on or all-off
        const isNonLeaf = childrenMap.has(p.id)
        const newSkippedCount = isNonLeaf ? (skipped ? p.totalCount : 0) : p.skippedCount
        return { ...p, skipped, skippedCount: newSkippedCount }
      }
      if (ancestorIds.has(p.id)) {
        // Recalculate skippedCount using pre-computed leaf descendants
        const leafDescendants = ancestorLeafMap.get(p.id) ?? []
        const newSkippedCount = leafDescendants.reduce((count, leaf) => {
          const willBeSkipped = idsToUpdate.has(leaf.id) ? skipped : leaf.skipped
          return count + (willBeSkipped ? 1 : 0)
        }, 0)
        return { ...p, skippedCount: newSkippedCount }
      }
      return p
    })

    // Optimistically write cache immediately
    cache.writeQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
      query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
      variables: queryVariables,
      data: {
        user: {
          __typename: 'User',
          id: userId,
          needsReview,
          parentalControlPreferences: optimisticPrefs,
        },
      },
    })

    try {
      await executeUpdate({
        variables: { language: lang, updates: [{ categoryId, skipped }], ...(contentId ? { contentId } : {}) },
        context: { requireAuth: true },
        update(updateCache, { data: mutationData }) {
          const updatedPreferences = mutationData?.updateParentalControlPreferences
          if (!updatedPreferences) return

          const currentData = updateCache.readQuery<
            GetSkipCategoryPreferencesQuery,
            GetSkipCategoryPreferencesQueryVariables
          >({
            query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
            variables: queryVariables,
          })

          const currentPrefs = currentData?.user?.parentalControlPreferences ?? []
          const updatedMap = new Map(updatedPreferences.filter(isDefined).map((p) => [p.id, p]))

          const mergedPreferences = currentPrefs.map((existing) => {
            if (!existing) return existing
            const updated = updatedMap.get(existing.id)
            if (!updated) return existing
            return { ...existing, ...updated, isNew: existing.isNew }
          })

          updateCache.writeQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
            query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
            variables: queryVariables,
            data: {
              user: {
                __typename: 'User',
                id: userId,
                needsReview,
                parentalControlPreferences: mergedPreferences,
              },
            },
          })
        },
      })
    } catch (err) {
      // Revert only the failed preference(s), preserving other in-flight optimistic updates.
      // We build a map of pre-mutation values for the IDs this call touched,
      // then read the *current* cache (which may include other optimistic writes) and
      // surgically restore only the affected entries.
      const preUpdateMap = new Map(
        existingPrefs.filter((p) => idsToUpdate.has(p.id) || ancestorIds.has(p.id)).map((p) => [p.id, p]),
      )

      const currentData = cache.readQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
        query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
        variables: queryVariables,
      })
      const currentPrefs = (currentData?.user?.parentalControlPreferences ?? []).filter(isDefined)

      const revertedPrefs = currentPrefs.map((p) => {
        const original = preUpdateMap.get(p.id)
        return original ?? p
      })

      cache.writeQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
        query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
        variables: queryVariables,
        data: {
          user: {
            __typename: 'User',
            id: userId,
            needsReview,
            parentalControlPreferences: revertedPrefs,
          },
        },
      })
      logger().error('Failed to update skip category preference', { lang, categoryId, skipped }, err)
    }
  }

  const totalCount = countLeaves(rawPreferences)
  const totalSkippedCount = countSkippedLeaves(rawPreferences)
  const hasAppliedControls = totalSkippedCount > 0

  return {
    loading,
    updating,
    error,
    preferences: orderedPreferences,
    allPreferences: rawPreferences,
    updateSkipPreference,
    refetch,
    totalSkippedCount,
    totalCount,
    hasAppliedControls,
  }
}
